import html2pdf from 'html2pdf.js';

const COVER_IMAGE_URL = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22600%22 height=%22200%22 viewBox=%220 0 600 200%22%3E%3Crect width=%22600%22 height=%22200%22 rx=%2224%22 fill=%22%23ffffff%22/%3E%3Ctext x=%22300%22 y=%22122%22 text-anchor=%22middle%22 font-family=%22Arial%2C%20Helvetica%2C%20sans-serif%22 font-size=%2264%22 font-weight=%22700%22 fill=%22%23063e50%22%3EProfitYo%3C/text%3E%3C/svg%3E';

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const buildAccountSections = (groups, formatCurrency, formatDate) => groups.map((group) => {
  const accountName = [group.account.code, group.account.name].filter(Boolean).join(' - ');
  const transactions = group.displayTransactions.length
    ? group.displayTransactions.map((entry) => `
      <tr>
        <td>${escapeHtml(formatDate(entry.date))}</td>
        <td>${escapeHtml(entry.description || 'System Entry')}</td>
        <td class="amount">${Number(entry.debit) ? escapeHtml(formatCurrency(entry.debit)) : '—'}</td>
        <td class="amount">${Number(entry.credit) ? escapeHtml(formatCurrency(entry.credit)) : '—'}</td>
        <td class="amount balance">${escapeHtml(formatCurrency(entry.runningBalance))}</td>
      </tr>
    `).join('')
    : '<tr><td colspan="5" class="empty">No transactions recorded in this period.</td></tr>';

  return `
    <section class="account-section">
      <div class="account-heading">
        <div>
          <p class="account-label">Account</p>
          <h2>${escapeHtml(accountName)}</h2>
        </div>
        <div class="account-summary">
          <span>Opening balance</span>
          <strong>${escapeHtml(formatCurrency(group.beginningBalance))}</strong>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th class="amount">Debit</th>
            <th class="amount">Credit</th>
            <th class="amount">Balance</th>
          </tr>
        </thead>
        <tbody>
          <tr class="balance-row">
            <td></td>
            <td>Beginning Balance</td>
            <td></td>
            <td></td>
            <td class="amount balance">${escapeHtml(formatCurrency(group.beginningBalance))}</td>
          </tr>
          ${transactions}
          <tr class="ending-balance-row">
            <td></td>
            <td>Ending Balance</td>
            <td></td>
            <td></td>
            <td class="amount balance">${escapeHtml(formatCurrency(group.endingBalance))}</td>
          </tr>
        </tbody>
      </table>
    </section>
  `;
}).join('');

const pdfStyles = `
  * { box-sizing: border-box; }
  .transaction-details-pdf {
    background: #ffffff;
    color: #123e50;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 10px;
    line-height: 1.45;
    width: 8.5in;
  }
  .cover {
    align-items: center;
    background: linear-gradient(145deg, #053f52 0%, #07586c 54%, #087c73 100%);
    break-after: page;
    color: #ffffff;
    display: flex;
    flex-direction: column;
    height: 11in;
    justify-content: center;
    overflow: hidden;
    page-break-after: always;
    padding: 0.7in;
    position: relative;
    text-align: center;
  }
  .cover::before,
  .cover::after {
    border: 1px solid rgba(129, 230, 217, 0.3);
    border-radius: 50%;
    content: "";
    position: absolute;
  }
  .cover::before {
    height: 6.8in;
    right: -2.7in;
    top: -2.5in;
    width: 6.8in;
  }
  .cover::after {
    bottom: -3.2in;
    height: 7.8in;
    left: -3.1in;
    width: 7.8in;
  }
  .cover-inner {
    max-width: 5.7in;
    position: relative;
    z-index: 1;
  }
  .brand-card {
    align-items: center;
    background: #ffffff;
    border-radius: 18px;
    display: flex;
    height: 1.65in;
    justify-content: center;
    margin: 0 auto 0.55in;
    overflow: hidden;
    padding: 0.1in;
    width: 4.7in;
  }
  .brand-card img {
    display: block;
    height: 100%;
    max-width: 100%;
    object-fit: contain;
  }
  .cover-kicker {
    color: #8fe2d5;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 2.5px;
    margin: 0 0 18px;
    text-transform: uppercase;
  }
  .cover h1 {
    font-size: 40px;
    font-weight: 700;
    letter-spacing: -1.15px;
    line-height: 1.08;
    margin: 0;
  }
  .cover-rule {
    background: #5ed1bc;
    height: 4px;
    margin: 26px auto;
    width: 68px;
  }
  .cover-business {
    font-size: 18px;
    font-weight: 700;
    margin: 0;
  }
  .cover-period {
    color: #d3f3ed;
    font-size: 12px;
    margin: 10px 0 0;
  }
  .cover-footer {
    bottom: 0.48in;
    color: rgba(255, 255, 255, 0.72);
    font-size: 9px;
    left: 0;
    letter-spacing: 0.8px;
    position: absolute;
    right: 0;
    text-transform: uppercase;
  }
  .report {
    min-height: 11in;
    padding: 0.48in 0.5in 0.55in;
  }
  .report-header {
    align-items: flex-end;
    border-bottom: 3px solid #087a72;
    display: flex;
    justify-content: space-between;
    margin: 0 0 23px;
    padding: 0 0 13px;
  }
  .report-title {
    color: #063e50;
    font-size: 23px;
    font-weight: 700;
    letter-spacing: -0.45px;
    line-height: 1.1;
    margin: 0 0 5px;
  }
  .business-name {
    color: #087a72;
    font-size: 12px;
    font-weight: 700;
    margin: 0;
  }
  .report-period {
    color: #506b76;
    font-size: 9px;
    margin: 5px 0 0;
  }
  .report-meta {
    color: #607985;
    font-size: 8px;
    text-align: right;
  }
  .report-meta strong {
    color: #174759;
    display: block;
    font-size: 9px;
    margin-bottom: 3px;
  }
  .account-section {
    break-inside: avoid;
    margin: 0 0 22px;
    page-break-inside: avoid;
  }
  .account-heading {
    align-items: center;
    background: #eff8f6;
    border-left: 5px solid #0b8b7f;
    display: flex;
    justify-content: space-between;
    padding: 10px 12px;
  }
  .account-label {
    color: #5a7881;
    font-size: 7.5px;
    font-weight: 700;
    letter-spacing: 1.25px;
    margin: 0 0 2px;
    text-transform: uppercase;
  }
  .account-heading h2 {
    color: #063e50;
    font-size: 11px;
    line-height: 1.2;
    margin: 0;
  }
  .account-summary {
    text-align: right;
  }
  .account-summary span {
    color: #5a7881;
    display: block;
    font-size: 7px;
    font-weight: 700;
    letter-spacing: 0.7px;
    text-transform: uppercase;
  }
  .account-summary strong {
    color: #063e50;
    font-size: 10px;
  }
  table {
    border-collapse: collapse;
    margin: 0;
    width: 100%;
  }
  th {
    background: #063e50;
    border-bottom: 1px solid #063e50;
    color: #ffffff;
    font-size: 7.5px;
    font-weight: 700;
    letter-spacing: 0.65px;
    padding: 7px 8px;
    text-align: left;
    text-transform: uppercase;
  }
  td {
    border-bottom: 1px solid #d7e6e5;
    color: #254c5a;
    padding: 7px 8px;
    vertical-align: top;
  }
  tbody tr:nth-child(even):not(.balance-row):not(.ending-balance-row) td {
    background: #fbfdfd;
  }
  .amount {
    text-align: right;
    white-space: nowrap;
  }
  .balance {
    color: #063e50;
    font-weight: 700;
  }
  .balance-row td {
    background: #f3f8f8;
    color: #496974;
    font-weight: 700;
  }
  .ending-balance-row td {
    background: #e1f3ef;
    border-bottom: 2px solid #0b8b7f;
    color: #063e50;
    font-weight: 700;
  }
  .empty {
    color: #647b83;
    font-style: italic;
    padding: 14px 8px;
    text-align: center;
  }
  .report-footer {
    border-top: 1px solid #bfd4d2;
    color: #698089;
    font-size: 7.5px;
    margin-top: 24px;
    padding-top: 8px;
    text-align: center;
  }
`;

const waitForImage = (image) => new Promise((resolve) => {
  if (image.complete) {
    resolve();
    return;
  }

  image.onload = resolve;
  image.onerror = resolve;
});

const waitForRender = () => new Promise((resolve) => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(resolve);
  });
});

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `${fileName}.pdf`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const validatePdfGroups = (groups) => {
  if (!Array.isArray(groups)) {
    throw new Error('Transaction Details PDF received an invalid dataset.');
  }

  const transactionCount = groups.reduce(
    (count, group) => count + (Array.isArray(group.displayTransactions)
      ? group.displayTransactions.length
      : 0),
    0
  );

  console.info('[Transaction Details PDF] Input validation', {
    groupCount: groups.length,
    transactionCount
  });

  if (groups.length === 0) {
    throw new Error('No account data is available for the PDF export.');
  }

  if (transactionCount === 0 && !groups.some((group) => (
    Number(group.beginningBalance) !== 0 || Number(group.endingBalance) !== 0
  ))) {
    throw new Error('No transaction records or balances are available for the PDF export.');
  }

  return transactionCount;
};

export async function downloadTransactionDetailsPdf({
  groups,
  businessName,
  fileName,
  periodStart,
  periodEnd,
  formatCurrency,
  formatDate
}) {
  const transactionCount = validatePdfGroups(groups);
  const generatedDate = new Date().toLocaleDateString();
  const container = document.createElement('div');

  container.className = 'transaction-details-pdf';
  container.style.position = 'absolute';
  container.style.left = '0';
  container.style.top = '0';
  container.style.display = 'block';
  container.style.width = '8.5in';
  container.style.minHeight = '11in';
  container.style.zIndex = '2147483647';
  container.style.pointerEvents = 'none';
  container.style.visibility = 'visible';
  container.style.opacity = '1';
  container.innerHTML = `
    <style>${pdfStyles}</style>
    <section class="cover">
      <div class="cover-inner">
        <div class="brand-card">
          <img src="${COVER_IMAGE_URL}" alt="">
        </div>
        <p class="cover-kicker">Financial Reporting Package</p>
        <h1>Transaction<br>Details</h1>
        <div class="cover-rule"></div>
        <p class="cover-business">${escapeHtml(businessName)}</p>
        <p class="cover-period">${escapeHtml(periodStart)} — ${escapeHtml(periodEnd)}</p>
      </div>
      <div class="cover-footer">Prepared ${escapeHtml(generatedDate)}</div>
    </section>
    <main class="report">
      <header class="report-header">
        <div>
          <h1 class="report-title">Transaction Details</h1>
          <p class="business-name">${escapeHtml(businessName)}</p>
          <p class="report-period">Reporting period: ${escapeHtml(periodStart)} to ${escapeHtml(periodEnd)}</p>
        </div>
        <div class="report-meta">
          <strong>Financial Statement</strong>
          Generated ${escapeHtml(generatedDate)}
        </div>
      </header>
      ${buildAccountSections(groups, formatCurrency, formatDate)}
      <footer class="report-footer">
        Transaction Details · ${escapeHtml(businessName)} · Generated ${escapeHtml(generatedDate)}
      </footer>
    </main>
  `;

  document.body.appendChild(container);

  try {
    const images = [...container.querySelectorAll('img')];
    await Promise.all(images.map(waitForImage));
    await waitForRender();

    const renderWidth = Math.max(container.offsetWidth, container.scrollWidth);
    const renderHeight = Math.max(container.offsetHeight, container.scrollHeight);

    if (!renderWidth || !renderHeight || !container.textContent.trim()) {
      throw new Error('The Transaction Details report could not be prepared for PDF export.');
    }

    const worker = html2pdf()
      .set({
        filename: `${fileName}.pdf`,
        image: {
          type: 'jpeg',
          quality: 0.98
        },
        html2canvas: {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: false,
          logging: false,
          scrollX: 0,
          scrollY: 0,
          windowWidth: renderWidth,
          windowHeight: renderHeight
        },
        jsPDF: {
          format: 'letter',
          orientation: 'portrait',
          unit: 'in',
          compress: true
        },
        margin: 0,
        pagebreak: {
          mode: ['css', 'legacy'],
          avoid: ['.account-section']
        }
      })
      .from(container);

    await worker.toPdf();

    const pdf = await worker.get('pdf');
    const pdfBlob = pdf.output('blob');

    if (!pdfBlob || pdfBlob.size === 0) {
      throw new Error('The PDF renderer generated an empty file.');
    }

    downloadBlob(pdfBlob, fileName);

    console.info('[Transaction Details PDF] PDF generated and downloaded', {
      groupCount: groups.length,
      transactionCount,
      canvasWidth: renderWidth,
      canvasHeight: renderHeight,
      pdfBytes: pdfBlob.size
    });
  } finally {
    container.remove();
  }
}